import { Group, Card as MantineCard, SimpleGrid, Text } from "@mantine/core";
import { createElement, type ReactNode } from "react";

import { resolveIcon } from "../icons";

interface CardGroupProps {
  children?: ReactNode;
  cols?: number;
}

/**
 * CardGroup renders the {% card-group %} tag as a responsive Mantine
 * `SimpleGrid`, narrowing to a single column on small viewports.
 */
export const CardGroup = ({ children, cols = 2 }: CardGroupProps) => (
  <SimpleGrid cols={{ base: 1, sm: cols }} my="md" spacing="md">
    {children}
  </SimpleGrid>
);

interface CardProps {
  children?: ReactNode;
  href?: string;
  icon?: string;
  title?: string;
}

/**
 * Card renders one {% card %} as a Mantine `Card`. With an `href` the whole card
 * is an anchor; an optional `icon` names a leading Nucleo glyph and the body is
 * the description. External targets open in a new tab safely.
 */
export const Card = ({ children, href, icon, title }: CardProps) => {
  const Icon = resolveIcon(icon);
  const isExternal = href ? /^https?:\/\//.test(href) : false;
  const anchorProps = href
    ? {
        component: "a" as const,
        href,
        rel: isExternal ? "noopener noreferrer" : undefined,
        target: isExternal ? "_blank" : undefined,
      }
    : {};

  return (
    <MantineCard padding="lg" {...anchorProps}>
      <Group mb={title ? "xs" : undefined}>
        {Icon ? createElement(Icon, { size: 20 }) : null}
        {title ? (
          <Text fw={600} size="lg">
            {title}
          </Text>
        ) : null}
      </Group>
      <Text c="dimmed" component="div">
        {children}
      </Text>
    </MantineCard>
  );
};
